import set from "lodash.set";

import {
  BlockTemplateDisplayName,
  BlockTemplateType,
  CheckoutBlockDto,
  CheckoutBlockType,
  getCheckoutBlockDisplayName,
  PostPurchaseUpsellCheckoutBlockDto,
  UpsellCheckoutBlockDto,
} from "@intelligems/ig-types/dtos/checkout-blocks";
import { CartTargetingDto } from "@intelligems/ig-types/dtos/cart-targeting";
import { CheckoutSectionStyle } from "@intelligems/ig-types/fe-only/checkout-blocks";
import { VariationDto } from "@intelligems/ig-types/dtos/variations";
import {
  generateInitialBlockStyles,
  generateInitialBlockVariables,
  mergeCheckoutBlockPatch,
} from "@intelligems/ig-utils/checkout-block-templates";
import { createSlice, current, type PayloadAction } from "@reduxjs/toolkit";
import { initialCheckoutBlockState } from "@src/api/initials";
import { newTrustBadgeItem } from "@src/features/sharedExperience/components/checkoutBlocks/CheckoutBlockSettings/TrustBadgeSettings/trustBadgeTemplateSettings";
import {
  blockTakesCheckoutUpsellDefaultRule,
  getRootCheckoutBlock,
} from "@src/store/slices/checkoutBlocks/selectors";
import {
  CartTesterProduct,
  CheckoutExtensionBlockDto,
  ICheckoutBlocksState,
} from "@src/store/slices/checkoutBlocks/types";
import { ensureBlockItemOrder } from "@src/store/slices/checkoutBlocks/utils";

// Auto-generated block name reusing the lowest free number so it stays sequential
const generateCheckoutBlockName = (
  blocks: CheckoutExtensionBlockDto[],
  variationId: string,
  displayName: string,
  excludeId?: string
): string => {
  const taken = new Set(
    blocks
      .filter(
        (block) =>
          block.id !== excludeId &&
          block.variationId === variationId &&
          getCheckoutBlockDisplayName(block) === displayName
      )
      .map((block) => block.name)
  );
  let n = 1;
  while (taken.has(`${displayName} ${n}`)) {
    n++;
  }
  return `${displayName} ${n}`;
};

/**
 * The block a rule binding can live on: a checkout upsell from
 * `checkoutBlocks`, or a post-purchase upsell from `postPurchaseBlocks` —
 * both arrays belong to this slice, keyed by the same block id space.
 */
const getAgenticCapableBlock = (
  draft: ICheckoutBlocksState,
  checkoutBlockId: string
): UpsellCheckoutBlockDto | PostPurchaseUpsellCheckoutBlockDto | undefined => {
  const checkoutBlock = getRootCheckoutBlock(draft, checkoutBlockId);
  if (checkoutBlock?.blockType === "upsell") {
    return checkoutBlock;
  }
  return draft.postPurchaseBlocks.find((b) => b.id === checkoutBlockId);
};

const initialState: ICheckoutBlocksState = {
  isSuccess: false,
  checkoutBlocks: [],
  storedCheckoutBlocks: [],
  currentCheckoutBlockId: null,
  lastAiCheckoutBlockId: null,
  postPurchaseBlocks: [],
  storedPostPurchaseBlocks: [],
  currentPostPurchaseBlockId: null,
  cartTesterProductsByVariation: {},
  cartTesterEditedByUserByVariation: {},
};

const checkoutBlocksSlice = createSlice({
  name: "checkoutBlocks",
  initialState,
  reducers: {
    resetCheckoutBlocks: () => {
      return initialState;
    },
    deleteCheckoutBlocks: (draft) => {
      draft.checkoutBlocks = [];
      return draft;
    },
    initCheckoutBlocksFromVariations: (
      draft,
      action: PayloadAction<{
        variations: VariationDto[];
      }>
    ) => {
      const { variations } = action.payload;

      const tempCheckoutBlocks: CheckoutExtensionBlockDto[] = [];
      const tempPostPurchaseBlocks: PostPurchaseUpsellCheckoutBlockDto[] = [];

      variations.forEach((variation) => {
        if (variation.checkoutBlocks) {
          tempCheckoutBlocks.push(
            ...(variation.checkoutBlocks.filter(
              (cb) => cb.blockType !== "postPurchaseUpsell"
            ) as CheckoutExtensionBlockDto[])
          );
          tempPostPurchaseBlocks.push(
            ...variation.checkoutBlocks.filter(
              (cb) => cb.blockType === "postPurchaseUpsell"
            )
          );
        }
      });

      if (tempCheckoutBlocks.length) {
        draft.isSuccess = true;
        draft.checkoutBlocks = tempCheckoutBlocks;
        draft.storedCheckoutBlocks = tempCheckoutBlocks;
      }
      if (tempPostPurchaseBlocks.length) {
        draft.isSuccess = true;
        draft.postPurchaseBlocks = tempPostPurchaseBlocks;
        draft.storedPostPurchaseBlocks = tempPostPurchaseBlocks;
      }
      return draft;
    },
    setCheckoutBlocksByVariationId: (
      draft,
      action: PayloadAction<{
        checkoutBlocks: CheckoutBlockDto[];
        variationId: string;
      }>
    ) => {
      const { variationId } = action.payload;
      const checkoutBlocks = action.payload.checkoutBlocks.filter(
        (cb): cb is CheckoutExtensionBlockDto =>
          cb.blockType !== "postPurchaseUpsell"
      );
      const updatedDraft = draft.checkoutBlocks.filter(
        (prod) => prod.variationId !== variationId
      );

      draft.checkoutBlocks = [...updatedDraft, ...checkoutBlocks];
    },
    initNewCheckoutBlock: (
      draft,
      action: PayloadAction<{
        variationId: string;
        blockType: CheckoutBlockType;
        template: BlockTemplateType;
        // Merchant's checkout section styling → seeds the new block's container
        // appearance so it matches the real checkout. Null on stores with no branding.
        sectionDefaults?: CheckoutSectionStyle | null;
      }>
    ) => {
      const initialBlock = initialCheckoutBlockState(
        action.payload.variationId,
        action.payload.blockType,
        action.payload.template,
        draft.checkoutBlocks.length,
        action.payload.sectionDefaults
      );
      // locationId keeps the global slot index; number the name per type.
      initialBlock.name = generateCheckoutBlockName(
        draft.checkoutBlocks,
        action.payload.variationId,
        BlockTemplateDisplayName[action.payload.template]
      );
      draft.checkoutBlocks.push(initialBlock);
      draft.currentCheckoutBlockId = initialBlock.id;
      return draft;
    },
    copyCheckoutBlock: (
      draft,
      action: PayloadAction<{ checkoutBlock: CheckoutExtensionBlockDto }>
    ) => {
      draft.checkoutBlocks.push(action.payload.checkoutBlock);
      return draft;
    },
    mergeCheckoutBlock: (
      draft,
      action: PayloadAction<{
        checkoutBlock: CheckoutExtensionBlockDto;
        patch: Record<string, unknown>;
      }>
    ) => {
      const existingIndex = draft.checkoutBlocks.findIndex(
        (block) => block.id === action.payload.checkoutBlock.id
      );
      if (existingIndex === -1) {
        draft.checkoutBlocks.push(action.payload.checkoutBlock);
      } else {
        draft.checkoutBlocks[existingIndex] = mergeCheckoutBlockPatch(
          current(draft.checkoutBlocks[existingIndex]),
          action.payload.patch
        );
      }
      draft.lastAiCheckoutBlockId = action.payload.checkoutBlock.id;
    },
    setCurrentCheckoutBlockId: (
      draft,
      action: PayloadAction<{ id: string }>
    ) => {
      draft.currentCheckoutBlockId = action.payload.id;
      return draft;
    },
    setCheckoutBlockVariable: (
      draft,
      action: PayloadAction<{
        checkoutBlockId: string;
        path: string;
        value: unknown;
      }>
    ) => {
      const { checkoutBlockId, path, value } = action.payload;
      const checkoutBlock = getRootCheckoutBlock(draft, checkoutBlockId);
      if (!checkoutBlock) {
        return;
      }

      set(checkoutBlock.variables, path, value);
    },
    setCheckoutBlockStyle: (
      draft,
      action: PayloadAction<{
        checkoutBlockId: string;
        path: string;
        value: unknown;
      }>
    ) => {
      const { checkoutBlockId, path, value } = action.payload;
      const checkoutBlock = getRootCheckoutBlock(draft, checkoutBlockId);
      if (!checkoutBlock) {
        return;
      }

      let processedValue = value;
      if (path === "layout.columnCount" || path === "layout.rowCount") {
        processedValue = Number(value);
      }

      set(checkoutBlock.styles, path, processedValue);
    },
    setCheckoutBlockNameOrType: (
      draft,
      action: PayloadAction<{
        checkoutBlockId: string;
        path: string;
        value: unknown;
      }>
    ) => {
      const { checkoutBlockId, path, value } = action.payload;
      const checkoutBlock = getRootCheckoutBlock(draft, checkoutBlockId);
      if (!checkoutBlock) {
        return;
      }
      set(checkoutBlock, path, value);
    },
    /**
     * Attach the block to an Agentic Logic rule, or detach it with null.
     *
     * Only the rule is held here. The placement and the (placement, variation)
     * assignment are derived by the backend during the experience save, so the
     * builder never has to invent an id or leave one orphaned when the user
     * navigates away without saving.
     */
    setCheckoutBlockAgenticLogicRule: (
      draft,
      action: PayloadAction<{
        checkoutBlockId: string;
        agenticLogicRuleId: string | null;
      }>
    ) => {
      const { checkoutBlockId, agenticLogicRuleId } = action.payload;
      const checkoutBlock = getAgenticCapableBlock(draft, checkoutBlockId);
      if (!checkoutBlock) {
        return;
      }
      checkoutBlock.agenticLogicRuleId = agenticLogicRuleId;
    },
    /**
     * Bind a NEW Checkout > Upsell block to the store's built-in rule, its
     * default. A no-op once the block no longer takes the default — it was
     * saved, the merchant picked a rule (or none) while the rule id was on its
     * way, or it stopped being an upsell block — so a late arrival never
     * overrides a choice.
     */
    bindCheckoutUpsellDefaultRule: (
      draft,
      action: PayloadAction<{
        checkoutBlockId: string;
        agenticLogicRuleId: string;
      }>
    ) => {
      const { checkoutBlockId, agenticLogicRuleId } = action.payload;
      const checkoutBlock = getRootCheckoutBlock(draft, checkoutBlockId);
      if (
        checkoutBlock?.blockType === "upsell" &&
        blockTakesCheckoutUpsellDefaultRule(draft, checkoutBlockId)
      ) {
        checkoutBlock.agenticLogicRuleId = agenticLogicRuleId;
      }
    },
    setCheckoutBlockDisplayConditions: (
      draft,
      action: PayloadAction<{
        checkoutBlockId: string;
        displayConditions: CartTargetingDto | undefined;
      }>
    ) => {
      const { checkoutBlockId, displayConditions } = action.payload;
      const checkoutBlock = getRootCheckoutBlock(draft, checkoutBlockId);
      if (!checkoutBlock) {
        return;
      }
      checkoutBlock.displayConditions = displayConditions;
    },
    changeCheckoutTemplateType: (
      draft,
      action: PayloadAction<{
        checkoutBlockId: string;
        blockType: CheckoutBlockType;
        templateType: BlockTemplateType;
      }>
    ) => {
      const { checkoutBlockId, blockType, templateType } = action.payload;
      const checkoutBlock = getRootCheckoutBlock(draft, checkoutBlockId);
      if (!checkoutBlock) {
        return;
      }

      const currentMaxProductLimit =
        checkoutBlock.blockType === "upsell"
          ? (checkoutBlock.variables as any).maxProductLimit
          : undefined;

      // Renumber the name for the new template, but only for not-yet-saved
      // blocks still using the auto-name — never touch a user-typed name.
      const isExistingBlock = draft.storedCheckoutBlocks.some(
        (block) => block.id === checkoutBlockId
      );
      const nameMatch = checkoutBlock.name.match(/^(.+) (\d+)$/);
      if (
        !isExistingBlock &&
        nameMatch &&
        nameMatch[1] === getCheckoutBlockDisplayName(checkoutBlock)
      ) {
        checkoutBlock.name = generateCheckoutBlockName(
          draft.checkoutBlocks,
          checkoutBlock.variationId,
          BlockTemplateDisplayName[templateType],
          checkoutBlock.id
        );
      }

      checkoutBlock.blockType = blockType;
      checkoutBlock.blockTemplate = templateType;

      checkoutBlock.styles = generateInitialBlockStyles(
        blockType,
        templateType
      );
      const newVariables = generateInitialBlockVariables(
        blockType,
        templateType
      );

      if (
        blockType === "upsell" &&
        templateType !== "singleProduct" &&
        currentMaxProductLimit !== undefined &&
        currentMaxProductLimit > 1
      ) {
        (newVariables as any).maxProductLimit = currentMaxProductLimit;
      }

      checkoutBlock.variables = newVariables;
    },
    addCheckoutBlockItem: (
      draft,
      action: PayloadAction<{
        checkoutBlockId: string;
      }>
    ) => {
      const { checkoutBlockId } = action.payload;
      const checkoutBlock = getRootCheckoutBlock(draft, checkoutBlockId);
      if (!checkoutBlock) {
        return;
      }

      if (checkoutBlock.blockType === "trustBadge") {
        checkoutBlock.variables.items.push(
          newTrustBadgeItem(
            checkoutBlock.variables.items.length,
            checkoutBlock.blockTemplate
          )
        );

        const columnCount = checkoutBlock.styles.layout.columnCount || 1;
        const rowCount = checkoutBlock.styles.layout.rowCount || 1;
        const totalItems = checkoutBlock.variables.items.length;

        const currentCapacity = columnCount * rowCount;
        if (totalItems > currentCapacity) {
          if (columnCount >= 3) {
            checkoutBlock.styles.layout.rowCount = rowCount + 1;
          } else {
            checkoutBlock.styles.layout.columnCount = columnCount + 1;
          }
        }
      }

      return draft;
    },
    deleteCheckoutBlockItem: (
      draft,
      action: PayloadAction<{
        checkoutBlockId: string;
        order: number;
      }>
    ) => {
      const { checkoutBlockId, order } = action.payload;
      const checkoutBlock = getRootCheckoutBlock(draft, checkoutBlockId);
      if (!checkoutBlock) {
        return;
      }

      if (checkoutBlock.blockType !== "trustBadge") {
        return;
      }

      checkoutBlock.variables.items = ensureBlockItemOrder(
        checkoutBlock.variables.items.filter((item) => item.order !== order)
      );

      const columnCount = checkoutBlock.styles.layout.columnCount || 1;
      const rowCount = checkoutBlock.styles.layout.rowCount || 1;
      const remainingItems = checkoutBlock.variables.items.length;
      const currentCapacity = columnCount * rowCount;
      if (remainingItems < currentCapacity) {
        if (rowCount > 1 && remainingItems <= columnCount * (rowCount - 1)) {
          checkoutBlock.styles.layout.rowCount = rowCount - 1;
        } else if (
          columnCount > 1 &&
          remainingItems <= (columnCount - 1) * rowCount
        ) {
          checkoutBlock.styles.layout.columnCount = columnCount - 1;
        }
      }

      return draft;
    },
    deleteCheckoutBlock: (
      draft,
      action: PayloadAction<{
        checkoutBlockId: string;
      }>
    ) => {
      const { checkoutBlockId } = action.payload;

      draft.checkoutBlocks = draft.checkoutBlocks.filter(
        (block) => block.id !== checkoutBlockId
      );
      return draft;
    },
    resetCheckoutBlocksByVariation: (
      draft,
      action: PayloadAction<{
        variationId: string;
      }>
    ) => {
      const { variationId } = action.payload;

      draft.checkoutBlocks = draft.checkoutBlocks.filter(
        (block) => block.variationId !== variationId
      );
      return draft;
    },
    initNewPostPurchaseBlock: (
      draft,
      action: PayloadAction<PostPurchaseUpsellCheckoutBlockDto>
    ) => {
      draft.postPurchaseBlocks.push(action.payload);
      draft.currentPostPurchaseBlockId = action.payload.id;
    },
    setPostPurchaseVariable: (
      draft,
      action: PayloadAction<{ blockId: string; path: string; value: unknown }>
    ) => {
      const block = draft.postPurchaseBlocks.find(
        (b) => b.id === action.payload.blockId
      );
      if (!block) {
        return;
      }
      set(block.variables, action.payload.path, action.payload.value);
      if (
        action.payload.path === "discountType" ||
        action.payload.path === "discountValue"
      ) {
        // The extension prices from the flat fields; Agentic Logic reads the
        // block discount. A dialog edit must update both representations.
        block.variables.discount = {
          ...block.variables.discount,
          discountType: block.variables.discountType,
          discountValue: block.variables.discountValue,
          compareAtPriceDisplay:
            block.variables.discount?.compareAtPriceDisplay ?? "none",
        };
      }
    },
    setPostPurchaseStyle: (
      draft,
      action: PayloadAction<{ blockId: string; path: string; value: unknown }>
    ) => {
      const block = draft.postPurchaseBlocks.find(
        (b) => b.id === action.payload.blockId
      );
      if (!block) {
        return;
      }
      set(block.styles, action.payload.path, action.payload.value);
    },
    setPostPurchaseName: (
      draft,
      action: PayloadAction<{ blockId: string; name: string }>
    ) => {
      const block = draft.postPurchaseBlocks.find(
        (b) => b.id === action.payload.blockId
      );
      if (!block) {
        return;
      }
      block.name = action.payload.name;
    },
    resetPostPurchaseBlocks: (draft) => {
      draft.postPurchaseBlocks = [];
      draft.storedPostPurchaseBlocks = [];
      draft.currentPostPurchaseBlockId = null;
    },
    resetPostPurchaseBlocksByVariation: (
      draft,
      action: PayloadAction<{ variationId: string }>
    ) => {
      const { variationId } = action.payload;
      draft.postPurchaseBlocks = draft.postPurchaseBlocks.filter(
        (block) => block.variationId !== variationId
      );
    },
    setCartTesterProducts: (
      draft,
      action: PayloadAction<{
        variationId: string;
        products: CartTesterProduct[];
      }>
    ) => {
      const { variationId, products } = action.payload;
      draft.cartTesterProductsByVariation[variationId] = products;
    },
    removeCartTesterProduct: (
      draft,
      action: PayloadAction<{ variationId: string; variantId: string }>
    ) => {
      const { variationId, variantId } = action.payload;
      const products = draft.cartTesterProductsByVariation[variationId];
      if (products) {
        draft.cartTesterProductsByVariation[variationId] = products.filter(
          (p) => p.variantId !== variantId
        );
      }
      draft.cartTesterEditedByUserByVariation[variationId] = true;
    },
    removeCartTesterProductsByProductId: (
      draft,
      action: PayloadAction<{ variationId: string; productId: string }>
    ) => {
      const { variationId, productId } = action.payload;
      const products = draft.cartTesterProductsByVariation[variationId];
      if (products) {
        draft.cartTesterProductsByVariation[variationId] = products.filter(
          (p) => p.id !== productId
        );
      }
      draft.cartTesterEditedByUserByVariation[variationId] = true;
    },
    setCartTesterProductSubscription: (
      draft,
      action: PayloadAction<{
        variationId: string;
        variantIds: string[];
        isSubscription: boolean;
      }>
    ) => {
      const { variationId, variantIds, isSubscription } = action.payload;
      const products = draft.cartTesterProductsByVariation[variationId];
      if (products) {
        products.forEach((p) => {
          if (variantIds.includes(p.variantId)) {
            p.isSubscription = isSubscription;
          }
        });
      }
      draft.cartTesterEditedByUserByVariation[variationId] = true;
    },
    clearCartTesterProducts: (
      draft,
      action: PayloadAction<{ variationId: string }>
    ) => {
      draft.cartTesterProductsByVariation[action.payload.variationId] = [];
    },
    clearAllCartTesterProducts: (draft) => {
      draft.cartTesterProductsByVariation = {};
      draft.cartTesterEditedByUserByVariation = {};
    },
    markCartTesterEditedByUser: (
      draft,
      action: PayloadAction<{ variationId: string }>
    ) => {
      draft.cartTesterEditedByUserByVariation[action.payload.variationId] =
        true;
    },
    resetCartTesterEditedByUser: (
      draft,
      action: PayloadAction<{ variationId: string }>
    ) => {
      draft.cartTesterEditedByUserByVariation[action.payload.variationId] =
        false;
    },
  },
  selectors: {
    getCheckoutBlocks: (state) => state.checkoutBlocks,
  },
});

export const {
  resetCheckoutBlocks,
  initCheckoutBlocksFromVariations,
  initNewCheckoutBlock,
  setCheckoutBlockVariable,
  copyCheckoutBlock,
  mergeCheckoutBlock,
  setCheckoutBlockNameOrType,
  setCheckoutBlockDisplayConditions,
  setCheckoutBlockAgenticLogicRule,
  bindCheckoutUpsellDefaultRule,
  setCheckoutBlockStyle,
  changeCheckoutTemplateType,
  addCheckoutBlockItem,
  deleteCheckoutBlockItem,
  deleteCheckoutBlock,
  deleteCheckoutBlocks,
  setCurrentCheckoutBlockId,
  resetCheckoutBlocksByVariation,
  setCheckoutBlocksByVariationId,
  initNewPostPurchaseBlock,
  setPostPurchaseVariable,
  setPostPurchaseStyle,
  setPostPurchaseName,
  resetPostPurchaseBlocks,
  resetPostPurchaseBlocksByVariation,
  setCartTesterProducts,
  removeCartTesterProduct,
  removeCartTesterProductsByProductId,
  setCartTesterProductSubscription,
  clearCartTesterProducts,
  clearAllCartTesterProducts,
  markCartTesterEditedByUser,
  resetCartTesterEditedByUser,
} = checkoutBlocksSlice.actions;

export const checkoutBlocksReducer = checkoutBlocksSlice.reducer;
export const { getCheckoutBlocks } = checkoutBlocksSlice.selectors;
